import {
  computeDepositPaidNis,
  deriveBuyerPaymentStatus,
  isNonTerminalInstructionStatus,
  isPaymentFullyCollected,
  isPisAllowedForPartyType
} from '@sollapay/domain/rules';
import {getTrustTypeStrategy} from '@sollapay/domain/trust-types';
import {DEFAULT_LINK_EXPIRY_DURATION, PaymentMethod} from '@sollapay/enums';
import {parseMoney} from '@sollapay/utils';

import type {BuyerTableRow} from '../types/buyers.types';
import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {
  GeneralSaleTransactionType,
  LinkExpiryDuration,
  PartyType,
  TrustPurpose
} from '@sollapay/enums';
import type {
  BuyerDTO,
  BuyerPurchaseDTO,
  FatcaCrsSupplementaryPayload,
  PaymentInstructionDTO
} from '@sollapay/types';
import type {TFunction} from 'i18next';

export {computeDepositPaidNis, deriveBuyerPaymentStatus, isPaymentFullyCollected};

export function getAmountToPayLabel(strategy: TrustTypeStrategy, t: TFunction): string {
  return strategy.showsCollectionCapHint
    ? t('view.buyers.list.columns.amountToPayCapped', {
        percentage: strategy.maxCollectionPercentage
      })
    : t('view.buyers.list.columns.amountToPay');
}

/** The FATCA/CRS tab reports its all-unchecked defaults on mount, so only a ticked declaration counts as input. */
export function hasSupplementaryInput(values: FatcaCrsSupplementaryPayload | null): boolean {
  return Object.values(values?.declarationValues ?? {}).some(Boolean);
}

/** Wraps a single buyer as a one-element array for APIs that take a buyer list (e.g. EditBuyerModal) */
export function toBuyerList(buyer: BuyerDTO | null): BuyerDTO[] | null {
  return buyer ? [buyer] : null;
}

/** Finds an item by id, falling back to the first item when the id is missing or not found */
export function findByIdOrFirst<T extends {id: string}>(
  list: T[] | null | undefined,
  id: string | null | undefined
): T | null {
  return list?.find(item => item.id === id) ?? list?.[0] ?? null;
}

/**
 * Delete is only unambiguous for a single-buyer deal — a joint deal's row must be opened to
 * remove a specific co-buyer from the detail page instead.
 */
export function canDeleteBuyerRow(buyers: BuyerDTO[], canDeleteBuyer: boolean): boolean {
  return canDeleteBuyer && buyers.length === 1 && (buyers[0]?.canDelete ?? false);
}

/** Returns true for instructions that are not in a terminal failure/cancellation state */
export function isNonTerminalInstruction(i: PaymentInstructionDTO): boolean {
  return isNonTerminalInstructionStatus(i.status);
}

export function findLatestInstruction(
  instructions: PaymentInstructionDTO[]
): PaymentInstructionDTO | undefined {
  let latest: PaymentInstructionDTO | undefined;
  let latestTime = -Infinity;

  for (const instruction of instructions) {
    const time = new Date(instruction.createdAt).getTime();
    if (time > latestTime) {
      latest = instruction;
      latestTime = time;
    }
  }

  return latest;
}

/** Sum of `amount` for non-terminal instructions — what's currently outstanding/requested */
export function sumActiveRequestedMoney(instructions: PaymentInstructionDTO[]) {
  return instructions
    .filter(isNonTerminalInstruction)
    .reduce((sum, i) => sum.add(parseMoney(i.amount)), parseMoney('0'));
}

export function sumActiveRequestedNis(instructions: PaymentInstructionDTO[]): string {
  return sumActiveRequestedMoney(instructions).toString();
}

/**
 * Computes how much can still be collected for a given purchase.
 * remaining = max collectable - already sent (non-terminal instructions)
 */
export function computeRemainingAmountNis(
  purchase: BuyerPurchaseDTO,
  payments: PaymentInstructionDTO[],
  maxCollectableNis: string
): string {
  const maxCollectable = parseMoney(maxCollectableNis);
  const purchaseInstructions = payments.filter(i => i.trustBuyerPurchaseId === purchase.id);
  const alreadySent = sumActiveRequestedMoney(purchaseInstructions);
  const remaining = maxCollectable.subtract(alreadySent);
  return remaining.toNumber() > 0 ? remaining.toString() : '0';
}

/**
 * True while there's still collectable room left for a purchase — gates the "Request Payment"
 * CTA. Always true for a trust type that doesn't enforce the collection cap (e.g. GENERAL_SALE) —
 * the asset price is a reference figure there, not a ceiling that can be "used up".
 */
export function canRequestMorePayment(
  purchase: BuyerPurchaseDTO,
  instructions: PaymentInstructionDTO[],
  maxCollectableNis: string,
  enforcesCollectionCap: boolean
): boolean {
  if (!enforcesCollectionCap) return true;
  return (
    parseMoney(computeRemainingAmountNis(purchase, instructions, maxCollectableNis)).toNumber() > 0
  );
}

/** The lawyer's method pick, cleared when it is PIS but the active buyer can't use PIS. */
export function resolveInitiationMethod(
  selected: PaymentMethod | '',
  partyType: PartyType | undefined
): PaymentMethod | '' {
  return selected === PaymentMethod.PIS && !isPisAllowedForPartyType(partyType) ? '' : selected;
}

export function toBuyerOption({id, fullName, partyType}: BuyerDTO) {
  return {id, fullName, partyType};
}

/** Anything the user picked or typed differs from what the flow pre-filled. */
export function isPaymentRequestDirty(opts: {
  initiationMethod: PaymentMethod | '';
  customAmountNis: string;
  expiryDuration: LinkExpiryDuration;
  selectedBuyerId: string;
  defaultBuyerId?: string;
  selectedAssetId?: string;
}): boolean {
  return (
    opts.initiationMethod !== '' ||
    opts.customAmountNis !== '' ||
    opts.expiryDuration !== DEFAULT_LINK_EXPIRY_DURATION ||
    opts.selectedBuyerId !== (opts.defaultBuyerId ?? '') ||
    (opts.selectedAssetId ?? '') !== ''
  );
}

/** Returns true when the "send payment" next button should be disabled */
export function isPaymentNextDisabled(opts: {
  initiationMethod: PaymentMethod | '';
  allowCustomAmount: boolean;
  customAmountNis: string;
  remainingAmountNis: string;
  enforcesCollectionCap: boolean;
}): boolean {
  if (!opts.initiationMethod) return true;
  if (!opts.allowCustomAmount) return false;
  const amount = parseMoney(opts.customAmountNis || '0');
  return (
    !opts.customAmountNis ||
    opts.customAmountNis === '0' ||
    amount.toNumber() <= 0 ||
    (opts.enforcesCollectionCap &&
      amount.toNumber() > parseMoney(opts.remainingAmountNis).toNumber())
  );
}

export function buildBuyerTableRows(
  buyers: BuyerDTO[],
  instructions: PaymentInstructionDTO[],
  trustPurpose: TrustPurpose | null,
  transactionType: GeneralSaleTransactionType | null
): BuyerTableRow[] {
  const strategy = getTrustTypeStrategy(trustPurpose, transactionType);

  // Use push (not spread) to avoid O(n²) allocations
  const instructionsByPurchase = new Map<string, PaymentInstructionDTO[]>();
  for (const instruction of instructions) {
    const existing = instructionsByPurchase.get(instruction.trustBuyerPurchaseId);
    if (existing) {
      existing.push(instruction);
    } else {
      instructionsByPurchase.set(instruction.trustBuyerPurchaseId, [instruction]);
    }
  }

  const buyersByPurchase = new Map<string, BuyerDTO[]>();
  for (const buyer of buyers) {
    for (const purchase of buyer.purchases) {
      const existing = buyersByPurchase.get(purchase.id);
      if (existing) {
        existing.push(buyer);
      } else {
        buyersByPurchase.set(purchase.id, [buyer]);
      }
    }
  }

  const rows: BuyerTableRow[] = [];
  const {allowsMultiplePurchases} = strategy;

  for (const [purchaseId, buyersForPurchase] of buyersByPurchase.entries()) {
    buyersForPurchase.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    const allBuyers = buyersForPurchase as [BuyerDTO, ...BuyerDTO[]];
    const purchase = allBuyers[0].purchases.find(p => p.id === purchaseId)!;

    const purchaseInstructions = instructionsByPurchase.get(purchase.id) ?? [];
    const maxCollectableNis = parseMoney(purchase.purchasePriceNis)
      .percentOf(strategy.maxCollectionPercentage)
      .toString();
    const depositPaidNis = computeDepositPaidNis(purchaseInstructions);
    const amountToPayNis = strategy
      .computeAmountDue({
        maxCollectable: parseMoney(maxCollectableNis),
        depositPaid: parseMoney(depositPaidNis)
      })
      .toString();
    const paymentStatus = deriveBuyerPaymentStatus(
      purchaseInstructions,
      depositPaidNis,
      maxCollectableNis
    );
    const canRequestPayment = canRequestMorePayment(
      purchase,
      purchaseInstructions,
      maxCollectableNis,
      strategy.enforcesCollectionCap
    );

    const rowBase = {
      purchase,
      amountToPayNis,
      depositPaidNis,
      paymentStatus,
      canRequestPayment
    };

    rows.push(
      ...buildRowsForPurchase(rowBase, allBuyers, purchaseInstructions, allowsMultiplePurchases)
    );
  }

  return rows;
}

function buildRowsForPurchase(
  rowBase: Omit<BuyerTableRow, 'buyers' | 'latestInstruction'>,
  allBuyers: [BuyerDTO, ...BuyerDTO[]],
  purchaseInstructions: PaymentInstructionDTO[],
  showsCoBuyersInOneRow: boolean
): BuyerTableRow[] {
  if (showsCoBuyersInOneRow) {
    return [
      {
        ...rowBase,
        buyers: allBuyers,
        latestInstruction: findLatestInstruction(purchaseInstructions)
      }
    ];
  }

  const instructionsByBuyer = new Map<string, PaymentInstructionDTO[]>();
  for (const instruction of purchaseInstructions) {
    if (!instruction.buyerPartyId) continue;
    const existing = instructionsByBuyer.get(instruction.buyerPartyId);
    if (existing) {
      existing.push(instruction);
    } else {
      instructionsByBuyer.set(instruction.buyerPartyId, [instruction]);
    }
  }

  return allBuyers.map(buyer => {
    const buyerInstructions = instructionsByBuyer.get(buyer.id) ?? [];
    const buyerDepositPaidNis = computeDepositPaidNis(buyerInstructions);
    return {
      // Overrides rowBase's purchase-wide deposit/status with this buyer's own figures —
      // there's no per-buyer share of the price, so "full" means their own requests are met.
      ...rowBase,
      depositPaidNis: buyerDepositPaidNis,
      paymentStatus: deriveBuyerPaymentStatus(
        buyerInstructions,
        buyerDepositPaidNis,
        sumActiveRequestedNis(buyerInstructions)
      ),
      buyers: [buyer],
      latestInstruction: findLatestInstruction(buyerInstructions)
    };
  });
}
